import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users2, TrendingUp, FileText, Contact, BarChart3,
  Package, Package2, Target, ShieldCheck, Settings, Truck, Building2, ClipboardList,
  Activity, ListChecks, PhoneCall, Gauge, Database, Receipt, Wrench, ChevronDown, UsersRound,
  Mail, Send, Megaphone, Network, Radio, CloudCog, PanelLeftClose, PanelLeftOpen, MapPin, ScanLine, BookOpen,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { NAV_GROUPS, ROLE_LABELS } from "@/lib/constants";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";

const ICONS = {
  LayoutDashboard, Users2, TrendingUp, FileText, Contact, BarChart3, Package, Package2,
  Target, ShieldCheck, Settings, Truck, Building2, ClipboardList, Activity, ListChecks,
  PhoneCall, Gauge, Database, Receipt, Wrench, UsersRound, Mail, Send, Megaphone, Network, Radio, CloudCog, MapPin, ScanLine, BookOpen,
};

export function SidebarContent({ onNavigate, collapsed, onToggleCollapse }) {
  const { user } = useAuth();
  const location = useLocation();

  const { data: mv } = useQuery({
    queryKey: ["menu-visibility"],
    queryFn: async () => (await api.get("/menu-visibility")).data,
    staleTime: 120000,
  });
  const ov = mv?.overrides || {};
  const groups = NAV_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((i) => user?.role === "super_admin" || (ov[i.to] || i.roles).includes(user?.role)) }))
    .filter((g) => g.items.length > 0);

  const activeGroup = groups.find((g) =>
    g.items.some((i) => (i.to === "/" ? location.pathname === "/" : location.pathname.startsWith(i.to))))?.key;
  const [open, setOpen] = useState(() => (activeGroup ? [activeGroup] : [groups[0]?.key].filter(Boolean)));

  const toggle = (key) => setOpen((o) => (o.includes(key) ? o.filter((k) => k !== key) : [...o, key]));

  if (collapsed) {
    return (
      <div className="flex flex-col h-full items-center text-slate-300" style={{ backgroundColor: "#231f20" }}>
        <div className="py-5 border-b border-white/10 w-full flex justify-center">
          <img src="/uvi-emblem-white.svg" alt="UniVicoustic" className="h-8 w-auto" />
        </div>
        <nav className="flex-1 py-4 space-y-1 overflow-y-auto scrollbar-thin w-full flex flex-col items-center">
          {groups.map((g) => {
            const GIcon = ICONS[g.icon];
            return (
              <button key={g.key} title={g.label}
                onClick={() => { onToggleCollapse?.(false); setOpen([g.key]); }}
                data-testid={`nav-rail-${g.key}`}
                className={`h-10 w-10 flex items-center justify-center rounded-md transition-colors ${
                  g.key === activeGroup ? "bg-white/[0.1] text-white" : "text-slate-400 hover:bg-white/[0.06] hover:text-white"
                }`}>
                {GIcon && <GIcon className="h-[18px] w-[18px] text-brand" />}
              </button>
            );
          })}
        </nav>
        <button onClick={() => onToggleCollapse?.(false)} title="Expand sidebar" data-testid="sidebar-expand-btn"
          className="mb-4 h-9 w-9 flex items-center justify-center rounded-md text-slate-400 hover:bg-white/[0.08] hover:text-white transition-colors">
          <PanelLeftOpen className="h-4.5 w-4.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full text-slate-300" style={{ backgroundColor: "#231f20" }}>
      <div className="px-4 py-5 border-b border-white/10">
        <div className="flex items-center gap-3" data-testid="sidebar-brand">
          <img src="/uvi-emblem-white.svg" alt="UniVicoustic" className="h-9 w-auto" />
          <span className="h-8 w-px bg-white/15" />
          <div className="leading-tight">
            <img src="/uvi-wordmark-white.svg" alt="UniVicoustic" className="h-3 w-auto" />
            <p className="text-[10px] text-slate-400 mt-1.5">Acoustic CRM · United Group</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1.5 overflow-y-auto scrollbar-thin">
        {groups.map((g) => {
          const GIcon = ICONS[g.icon];
          const isOpen = open.includes(g.key);
          const groupActive = g.key === activeGroup;
          return (
            <div key={g.key}>
              <button
                onClick={() => toggle(g.key)}
                data-testid={`nav-group-${g.key}`}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-md text-sm font-semibold tracking-wide transition-colors ${
                  groupActive && !isOpen ? "bg-white/[0.08] text-white" : "text-slate-200 hover:bg-white/[0.05] hover:text-white"
                }`}
              >
                <span className="flex items-center gap-3">
                  {GIcon && <GIcon className="h-[18px] w-[18px] text-brand" />}
                  {g.label}
                </span>
                <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="mt-0.5 mb-1 ml-3 pl-3 border-l border-white/10 space-y-0.5">
                  {g.items.map((item) => {
                    const Icon = ICONS[item.icon];
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.to === "/"}
                        onClick={onNavigate}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={({ isActive }) =>
                          `flex items-center gap-2.5 px-3 py-1.5 rounded-md text-[13px] font-medium border-l-2 -ml-[13px] pl-[23px] transition-colors ${
                            isActive
                              ? "bg-white/[0.08] text-white border-brand"
                              : "text-slate-400 border-transparent hover:bg-white/[0.05] hover:text-white"
                          }`
                        }
                      >
                        {Icon && <Icon className="h-4 w-4" />}
                        {item.label}
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="px-4 py-3 border-t border-white/10 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-medium text-white truncate">{user?.name}</p>
          <p className="text-[11px] text-slate-400">{ROLE_LABELS[user?.role]}</p>
        </div>
        {onToggleCollapse && (
          <button onClick={() => onToggleCollapse(true)} title="Collapse sidebar — full-screen dashboards" data-testid="sidebar-collapse-btn"
            className="h-8 w-8 shrink-0 flex items-center justify-center rounded-md text-slate-400 hover:bg-white/[0.08] hover:text-white transition-colors">
            <PanelLeftClose className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
